import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, Check, Loader2, PanelLeft, Download, Tv2, ScrollText, Image as ImageIcon, Film, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { API, imageUrl } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import StoryBoard from "@/components/StoryBoard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const EMPTY = {
  project_id: null,
  title: "",
  idea: "",
  description: "",
  scenes: [],
  style_image_id: null,
  style_description: "",
  references: [],
  thumbnail_prompt: "",
  scene_prompts: [],
  thumbnail_image_id: null,
  thumbnail_video_url: null,
  thumbnail_video_approved: false,
  scene_image_ids: [],
  scene_video_urls: [],
  scene_dialogs: [],
  scene_approved: [],
  scene_image_approved: [],
  format: "long",
  channel_id: null,
  channel_name: "",
  tone: "",
};

const payloadOf = (p) => {
  const { project_id, created_at, updated_at, user_id, ...rest } = p;
  const body = { ...EMPTY, ...rest, project_id: undefined };
  // These arrays are RACE-PRONE and owned by atomic backend writes (video job + /scene-flag).
  // Never send them in a full-document save — a stale copy would truncate them and break
  // assembly (the "8-second video" bug). The backend PUT ignores them anyway.
  delete body.scene_video_urls;
  delete body.scene_approved;
  delete body.scene_image_approved;
  delete body.thumbnail_video_url;
  return body;
};

// Turn raw backend / third-party provider errors into clear, friendly messages.
const friendlyError = (e, fallback = "Something went wrong. Please try again.") => {
  const raw = (e?.response?.data?.detail || e?.message || "").toString();
  const low = raw.toLowerCase();
  if (low.includes("approved") && low.includes("locked")) {
    return "This scene is approved and locked. Un-approve it first (click the green Approved button), then generate/regenerate.";
  }
  // OUT OF CREDITS / INSUFFICIENT BALANCE — checked FIRST and made provider-specific,
  // so a "402 insufficient balance" isn't mislabeled as a generic outage.
  if (low.includes("enough credits") || low.includes("out of credits") || low.includes("insufficient balance")
      || low.includes("insufficient credit") || low.includes("insufficient funds") || low.includes(" 402")
      || low.includes("error 402") || low.includes("insufficient") || low.includes("quota")
      || low.includes("billing") || low.includes("out of credit")) {
    if (low.includes("runway"))
      return "Your Runway account is out of credits. Top it up at dev.runwayml.com (Billing / Credits), then try generating the video again.";
    if (low.includes("atlas") || low.includes("seedance"))
      return "Your Seedance/Atlas account has insufficient balance (out of credits). Add funds to that account, then try again.";
    if (low.includes("grok") || low.includes("xai"))
      return "Your Grok (xAI) account is out of credits. Add balance, then try again.";
    if (low.includes("openai") || low.includes("gpt-image") || low.includes("images/"))
      return "Your OpenAI account is out of credits/quota. Add balance to your OpenAI API key, then try again.";
    return "The AI provider account is out of credits / has insufficient balance. Add funds to that provider, then try again.";
  }
  // Video provider content moderation
  if (low.includes("nsfw") || low.includes("unsafe") || low.includes("flagged") || low.includes("content filter") || low.includes("moderation") || low.includes("safety")) {
    return "The video provider's content filter rejected this scene's image or prompt (often a false-positive). Regenerate the scene image, or tweak the prompt, then try again.";
  }
  // Runway not yet enabled on the (deployed) server — needs a fresh deploy to pick up the key.
  if (low.includes("runway isn't enabled") || low.includes("runwayml_api_secret")) {
    return "Runway isn't enabled on your published site yet. Click Deploy to publish the latest version of the app (it includes Runway), then try the video again.";
  }
  // Grok not enabled
  if (low.includes("grok isn't enabled") || low.includes("xai_api_key")) {
    return "Grok isn't enabled on this server yet. Click Deploy to publish the latest version, then try again.";
  }
  // Atlas Cloud / Seedance video provider outages (NOT credit issues — those are handled above)
  if (low.includes("atlas") || low.includes("assetd_sync_jobs") || low.includes("queued model history") || low.includes("1146")) {
    return "The video provider (Seedance/Atlas) is temporarily unavailable — this is on their end, not your story. Please wait a few minutes and try again.";
  }
  if (low.includes("no reason") || low.includes("provider returned")) {
    return "The video provider rejected this scene without giving a reason — usually a temporary glitch on their end. Please try again; if it keeps failing, regenerate the scene image.";
  }
  if (low.includes("timed out") || low.includes("timeout")) {
    return "This is taking longer than expected and timed out. The provider may be busy — please try again in a moment.";
  }
  if (low.includes("rate limit") || low.includes("429")) {
    return "Too many requests right now. Please wait a minute and try again.";
  }
  if (low.includes("input_fidelity") || low.includes("gpt-image") || low.includes("images/edits") || low.includes("openai")) {
    return "The image AI rejected this request. Please try again, or tweak your prompt/text.";
  }
  if (low.includes("502") || low.includes("503") || low.includes("504") || low.includes("gateway")) {
    return "The service is briefly unreachable. Please try again in a moment.";
  }
  // Otherwise show the real provider message (Runway/Seedance detail), trimmed — never a vague generic.
  return raw && raw.length < 300 ? raw : fallback;
};

// Longer-lived error toast so users can read provider messages.
const errorToast = (e, fallback) => toast.error(friendlyError(e, fallback), { duration: 15000 });

export default function Studio() {
  const [projects, setProjects] = useState([]);
  const [channels, setChannels] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [project, setProject] = useState(EMPTY);
  const [generating, setGenerating] = useState(false);
  const [regenKeys, setRegenKeys] = useState([]);
  const [assembling, setAssembling] = useState(false);
  const [assembleProgress, setAssembleProgress] = useState({ pct: 0, stage: "" });
  const [assembleElapsed, setAssembleElapsed] = useState(0);
  const [assembleLogs, setAssembleLogs] = useState([]);
  const [assembleError, setAssembleError] = useState("");
  const [assembleDone, setAssembleDone] = useState(false);
  const [finalVideoDuration, setFinalVideoDuration] = useState(0);
  const [finalVideoUrl, setFinalVideoUrl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const openLogs = async () => {
    if (!project.project_id) { toast.error("Save the story first"); return; }
    setLogsOpen(true);
    setLogsLoading(true);
    try {
      const { data } = await api.get(`/prompt-logs`, { params: { project_id: project.project_id } });
      setLogs(data.logs || []);
    } catch (e) {
      toast.error("Could not load prompt log");
    } finally {
      setLogsLoading(false);
    }
  };
  const copyText = (t) => { navigator.clipboard.writeText(t || ""); toast.success("Prompt copied"); };
  const savingRef = useRef(false);
  const projectRef = useRef(project);
  const videoJobsRef = useRef({});  // index -> { jobId, cancelled } for in-progress scene videos
  useEffect(() => { projectRef.current = project; }, [project]);

  // Assembling timer (elapsed seconds while a video is being assembled).
  useEffect(() => {
    if (!assembling) return;
    setAssembleElapsed(0);
    const t = setInterval(() => setAssembleElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [assembling]);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch {
      toast.error("Could not load stories");
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const loadChannels = useCallback(async () => {
    try {
      const { data } = await api.get("/channels");
      setChannels(data);
    } catch { /* channels optional */ }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  // Honor navigation intents coming from the Dashboard (open a story / start a story from a channel).
  const location = useLocation();
  const navigate = useNavigate();
  const intentHandledRef = useRef(false);
  useEffect(() => {
    const st = location.state;
    if (!st || intentHandledRef.current) return;
    if (st.newIdea) {
      intentHandledRef.current = true;
      setProject({ ...EMPTY, idea: st.newIdea, format: st.newFormat || "long" });
      setDirty(true);
      toast.success("New story started from your niche");
      navigate("/studio", { replace: true, state: {} });
    } else if (st.openProjectId) {
      intentHandledRef.current = true;
      selectProject(st.openProjectId);
      navigate("/studio", { replace: true, state: {} });
    } else if (st.newFromChannelId && channels.length) {
      intentHandledRef.current = true;
      const ch = channels.find((c) => c.channel_id === st.newFromChannelId);
      newProject(ch || null);
      navigate("/studio", { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, channels]);

  const setField = (key, value) => {
    setProject((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const saveProject = useCallback(async (override) => {
    const current = override || project;
    savingRef.current = true;
    setSaving(true);
    try {
      const body = payloadOf(current);
      let saved;
      if (current.project_id) {
        const { data } = await api.put(`/projects/${current.project_id}`, body);
        saved = data;
      } else {
        const { data } = await api.post("/projects", body);
        saved = data;
      }
      setProject((prev) => ({ ...prev, project_id: saved.project_id, created_at: saved.created_at, updated_at: saved.updated_at }));
      setDirty(false);
      loadProjects();
      return saved;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      return null;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [project, loadProjects]);

  // Auto-save: debounced, no manual Save click required.
  useEffect(() => {
    if (!dirty || savingRef.current) return;
    if (regenKeys.length > 0) return;
    const hasContent = (project.idea && project.idea.trim()) || project.project_id;
    if (!hasContent) return;
    const t = setTimeout(() => {
      if (!savingRef.current) saveProject();
    }, 1500);
    return () => clearTimeout(t);
  }, [dirty, project, regenKeys, saveProject]);

  const newProject = (channel) => {
    if (channel && channel.channel_id) {
      setProject({
        ...EMPTY,
        references: (channel.references || []).slice(0, 10),
        style_description: channel.style_description || "",
        tone: channel.tone || "",
        channel_id: channel.channel_id,
        channel_name: channel.name || "",
      });
      setDirty(true);
      toast.success(`Started from “${channel.name}”`);
    } else {
      setProject(EMPTY);
      setDirty(false);
    }
  };

  const assignChannel = async (channel) => {
    if (!channel) return;
    const merged = [...(project.references || [])];
    const labelKey = (r) => `${r.type}:${(r.label || "").trim().toLowerCase()}`;
    const have = new Set(merged.map(labelKey));
    for (const r of channel.references || []) {
      if (merged.length >= 10) break;
      if (have.has(labelKey(r))) continue; // skip same-name/type refs (avoids duplicate "Bella" etc.)
      merged.push(r);
      have.add(labelKey(r));
    }
    const updated = {
      ...project,
      references: merged,
      style_description: project.style_description || channel.style_description || "",
      tone: channel.tone || project.tone || "",
      channel_id: channel.channel_id,
      channel_name: channel.name || "",
    };
    setProject(updated);
    await saveProject(updated);
    toast.success(`Assigned to “${channel.name}”`);
  };

  // Pick a channel for a NEW (not-yet-saved) story — applies its refs/style/tone.
  const selectChannelForNew = (channelId) => {
    const ch = channels.find((c) => c.channel_id === channelId);
    if (!ch) return;
    const updated = {
      ...projectRef.current,
      references: (ch.references || []).slice(0, 10),
      style_description: ch.style_description || projectRef.current.style_description || "",
      tone: ch.tone || projectRef.current.tone || "",
      channel_id: ch.channel_id,
      channel_name: ch.name || "",
    };
    projectRef.current = updated;
    setProject(updated);
    setDirty(true);
  };

  const selectProject = async (id) => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject({ ...EMPTY, ...data });
      setDirty(false);
      setFinalVideoUrl(null);
      setFinalVideoDuration(0);
      try {
        const { data: fv } = await api.get(`/final-video-exists/${id}`);
        if (fv?.exists) { setFinalVideoUrl(`${API}/final-video/${id}?t=${Date.now()}`); setFinalVideoDuration(fv.duration || 0); }
      } catch { /* ignore */ }
    } catch {
      toast.error("Could not open project");
    }
  };

  const togglePublish = async () => {
    if (!project.project_id) return;
    const next = !project.published;
    try {
      await api.post(`/projects/${project.project_id}/publish`, { published: next });
      setProject((p) => ({ ...p, published: next }));
      loadProjects();
      toast.success(next ? "Story marked as Published" : "Story moved back to draft");
    } catch {
      toast.error("Could not update publish status");
    }
  };

  const deleteProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      if (project.project_id === id) newProject();
      loadProjects();
      toast.success("Story deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const downloadPackage = async () => {
    let current = project;
    if (dirty || !current.project_id) {
      current = await saveProject(current);
      if (!current) return;
    }
    setExporting(true);
    try {
      const res = await api.get(`/projects/${current.project_id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(current.title || current.idea || "video_package").slice(0, 40).replace(/[^a-z0-9]+/gi, "_")}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Package downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // Starts an async text job (generate-text / build-prompts) and polls until done → no proxy/Cloudflare timeout.
  const runTextJob = async (endpoint, body) => {
    const { data } = await api.post(endpoint, body);
    const jobId = data.job_id;
    const maxTries = 100; // ~5 min at 3s
    for (let i = 0; i < maxTries; i++) {
      await sleep(3000);
      try {
        const res = await api.get(`/text-job/${jobId}`);
        if (res.data.status === "done") return res.data.result;
        if (res.data.status === "error") throw new Error(res.data.error || "generation failed");
      } catch (e) {
        if (e?.message && !e?.response) throw e; // job error
        // transient poll error — keep polling
      }
    }
    throw new Error("timed out");
  };

  const generateDialogs = async () => {
    const scenes = project.scenes || [];
    if (scenes.length === 0) { toast.error("Generate or add scenes first"); return; }
    setGenerating(true);
    try {
      const data = await runTextJob("/generate-dialog", {
        title: project.title || project.idea || "",
        description: project.description || "",
        scenes,
        tone: project.tone || "",
      });
      const updated = { ...projectRef.current, scene_dialogs: data.dialogs || [] };
      setProject(updated);
      toast.success("Dialog generated for all scenes");
      await saveProject(updated);
    } catch (e) {
      errorToast(e, "Dialog generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const generateText = async () => {
    setGenerating(true);
    try {
      const data = await runTextJob("/generate-text", { idea: project.idea, tone: project.tone || "" });
      const updated = { ...project, title: data.title, description: data.description, scenes: data.scenes || [], scene_dialogs: data.dialogs || [] };
      setProject(updated);
      toast.success("Title, description & scenes generated");
      await saveProject(updated);
    } catch (e) {
      errorToast(e, "Text generation failed");
    } finally {
      setGenerating(false);
    }
  };

  // Starts an async image job and polls until done/error. Short requests only → no proxy timeout.
  const generateOneImage = async (payload) => {
    const { data } = await api.post("/generate-image", payload);
    const jobId = data.job_id;
    const maxTries = 90; // ~4.5 min at 3s
    for (let i = 0; i < maxTries; i++) {
      await sleep(3000);
      try {
        const res = await api.get(`/image-job/${jobId}`);
        if (res.data.status === "done") return res.data.image_id;
        if (res.data.status === "error") throw new Error(res.data.error || "generation failed");
      } catch (e) {
        if (e?.message && !e?.response) throw e; // job error
        // transient network/poll error — keep polling
      }
    }
    throw new Error("timed out");
  };

  const regenerateOne = async (kind, index, promptText, characterIds, override) => {
    let current = project;
    if (!current.project_id) {
      current = await saveProject(current);
      if (!current) return;
    }
    const prompt = (promptText || "").trim();
    if (!prompt) {
      toast.error("Add some text for this image first");
      return;
    }
    const key = `${kind}-${index ?? 0}`;
    setRegenKeys((prev) => [...new Set([...prev, key])]);
    try {
      const seenRef = new Set();
      const dedupRefIds = (projectRef.current.references || [])
        .filter((r) => {
          const k = `${r.type}:${(r.label || "").trim().toLowerCase()}`;
          if (seenRef.has(k)) return false;
          seenRef.add(k);
          return true;
        })
        .map((r) => r.image_id);
      const imageId = await generateOneImage({
        project_id: current.project_id,
        kind,
        prompt,
        index: index ?? 0,
        reference_image_ids: dedupRefIds,
        style_description: projectRef.current.style_description || "",
        character_image_ids: characterIds || [],
        prompt_override: (override && override.trim()) ? override.trim() : null,
      });
      const base = projectRef.current;
      let np;
      if (kind === "thumbnail") {
        np = { ...base, thumbnail_image_id: imageId };
      } else {
        const arr = [...(base.scene_image_ids || [])];
        arr[index] = imageId;
        np = { ...base, scene_image_ids: arr };
      }
      setProject(np);
      await saveProject(np);
      toast.success(kind === "thumbnail" ? "Thumbnail regenerated" : `Scene ${index + 1} image regenerated`);
    } catch (e) {
      errorToast(e, "Regeneration failed");
    } finally {
      setRegenKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  const editSceneImage = async (kind, index, instruction) => {
    const instr = (instruction || "").trim();
    if (!instr) { toast.error("Describe what to fix"); return; }
    let current = project;
    if (!current.project_id) { current = await saveProject(current); if (!current) return; }
    const srcId = kind === "thumbnail" ? current.thumbnail_image_id : (current.scene_image_ids || [])[index];
    if (!srcId) { toast.error("Generate the image first"); return; }
    const key = `${kind}-${index ?? 0}`;
    setRegenKeys((prev) => [...new Set([...prev, key])]);
    try {
      const { data } = await api.post("/edit-image", {
        project_id: current.project_id, image_id: srcId, instruction: instr, kind, index: index ?? 0,
      });
      const jobId = data.job_id;
      let imageId = null;
      for (let i = 0; i < 90; i++) {
        await sleep(3000);
        try {
          const res = await api.get(`/image-job/${jobId}`);
          if (res.data.status === "done") { imageId = res.data.image_id; break; }
          if (res.data.status === "error") throw new Error(res.data.error || "edit failed");
        } catch (e) { if (e?.message && !e?.response) throw e; }
      }
      if (!imageId) throw new Error("timed out");
      const base = projectRef.current;
      let np;
      if (kind === "thumbnail") np = { ...base, thumbnail_image_id: imageId };
      else { const arr = [...(base.scene_image_ids || [])]; arr[index] = imageId; np = { ...base, scene_image_ids: arr }; }
      setProject(np);
      await saveProject(np);
      toast.success("Image edited");
    } catch (e) {
      errorToast(e, "Image edit failed");
    } finally {
      setRegenKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  const uploadUserImage = async (file, kind, index = 0) => {
    if (!file) return;
    let current = project;
    if (!current.project_id) {
      current = await saveProject(current);
      if (!current) return;
    }
    const key = `${kind}-${index ?? 0}`;
    setRegenKeys((prev) => [...new Set([...prev, key])]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", current.project_id);
      fd.append("kind", kind);
      fd.append("index", String(index ?? 0));
      const { data } = await api.post("/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const base = projectRef.current;
      let np;
      if (kind === "thumbnail") {
        np = { ...base, thumbnail_image_id: data.image_id };
      } else {
        const arr = [...(base.scene_image_ids || [])];
        while (arr.length <= index) arr.push(null);
        arr[index] = data.image_id;
        np = { ...base, scene_image_ids: arr };
      }
      setProject(np);
      await saveProject(np);
      toast.success(kind === "thumbnail" ? "Thumbnail uploaded" : `Scene ${index + 1} image uploaded`);
    } catch (e) {
      errorToast(e, "Image upload failed");
    } finally {
      setRegenKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  const uploadUserVideo = async (index, file) => {
    let current = projectRef.current;
    if (!current.project_id) {
      current = await saveProject(current);
      if (!current) return;
    }
    const key = `video-${index}`;
    setRegenKeys((prev) => [...new Set([...prev, key])]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", current.project_id);
      fd.append("index", String(index));
      const { data } = await api.post("/upload-scene-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const arr = [...(projectRef.current.scene_video_urls || [])];
      while (arr.length <= index) arr.push(null);
      arr[index] = data.video_url;
      const updated = { ...projectRef.current, scene_video_urls: arr };
      projectRef.current = updated;
      setProject(updated);
      toast.success(`Scene ${index + 1} video replaced`);
    } catch (e) {
      errorToast(e, "Video upload failed");
    } finally {
      setRegenKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  const buildBasePrompt = (kind, index, mods = "") => {
    const m = (mods || "").trim();
    if (kind === "thumbnail") {
      const title = project.title || project.idea || "";
      const isShort = project.format === "short";
      const ratioText = isShort
        ? "9:16 VERTICAL (portrait, full-height) YouTube Shorts thumbnail"
        : "16:9 YouTube thumbnail";
      let prompt =
        `Create a high-CTR, eye-catching ${ratioText} for a video titled "${title}". ` +
        `Feature the provided character/reference image(s) EXACTLY as they appear — same person/subject, face, ` +
        `hair, skin, body, colors and clothing; do NOT alter their appearance or add anything not already in their reference image. ` +
        `Match the EXACT visual style, medium and level of realism of the reference image(s): if the reference is a ` +
        `photorealistic photo, the thumbnail MUST be photorealistic (never cartoon, animated or illustrated); if it is an ` +
        `illustration or 3D render, match that instead. ` +
        `Use dramatic high-contrast lighting, a clean compelling composition, a background that fits the video's topic, ` +
        `and bold, legible thumbnail title text placed without covering faces.`;
      if (m) prompt += `\n\nADDITIONAL CHANGES REQUESTED (apply these): ${m}`;
      return prompt;
    }
    const desc = ((project.scenes || [])[index] || "").trim();
    return m ? `${desc}\n\nADDITIONAL CHANGES REQUESTED (apply these): ${m}` : desc;
  };

  const composeImagePrompt = async (kind, index, mods, characterIds) => {
    let current = project;
    if (!current.project_id) { current = await saveProject(current); if (!current) return ""; }
    const seenRef = new Set();
    const dedupRefIds = (projectRef.current.references || [])
      .filter((r) => {
        const k = `${r.type}:${(r.label || "").trim().toLowerCase()}`;
        if (seenRef.has(k)) return false; seenRef.add(k); return true;
      })
      .map((r) => r.image_id);
    const { data } = await api.post("/compose-image-prompt", {
      project_id: current.project_id,
      kind,
      prompt: buildBasePrompt(kind, index, mods),
      index: index ?? 0,
      reference_image_ids: dedupRefIds,
      style_description: projectRef.current.style_description || "",
      character_image_ids: characterIds || [],
    });
    return data.prompt || "";
  };

  const generateScene = (index, mods = "", characterIds, override) => {
    const desc = ((project.scenes || [])[index] || "").trim();
    if (!desc) {
      toast.error("Add a scene description first");
      return;
    }
    regenerateOne("scene", index, buildBasePrompt("scene", index, mods), characterIds, override);
  };

  // Every character reference available to this story: its own refs + the assigned channel's
  // current character refs (deduped by name). Used to guarantee thumbnails feature the cast.
  const allCharacterImageIds = () => {
    const cur = projectRef.current;
    const chRefs = (channels.find((c) => c.channel_id === cur.channel_id)?.references) || [];
    const seen = new Set();
    const ids = [];
    for (const r of [...(cur.references || []), ...chRefs]) {
      if (r.type !== "character" || !r.image_id) continue;
      const k = (r.label || "").trim().toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      ids.push(r.image_id);
    }
    return ids;
  };

  const regenerateThumbnail = (mods = "", characterIds, override) => {
    // A thumbnail should always feature the channel's cast, even when the title doesn't name them.
    const ids = characterIds && characterIds.length ? characterIds : allCharacterImageIds();
    regenerateOne("thumbnail", 0, buildBasePrompt("thumbnail", 0, mods), ids, override);
  };

  const generateSceneVideo = async (index, duration = 15, resolution = "1080p", modelChoice = "2.0", mods = "") => {
    const imgId = (project.scene_image_ids || [])[index];
    if (!imgId) {
      toast.error("Generate the scene image first");
      return;
    }
    let current = project;
    if (dirty || !current.project_id) {
      current = await saveProject(current);
      if (!current) return;
    }
    const key = `video-${index}`;
    setRegenKeys((k) => [...k, key]);
    try {
      const baseDesc = (current.scenes || [])[index] || "";
      const m = (mods || "").trim();
      const prompt = m ? `${baseDesc}\n\nADDITIONAL CHANGES REQUESTED (apply these to the motion/video): ${m}` : baseDesc;
      const { data } = await api.post("/generate-video", {
        project_id: current.project_id,
        index,
        image_id: imgId,
        prompt,
        dialog: (current.scene_dialogs || [])[index] || "",
        model_choice: modelChoice,
        duration,
        resolution,
      });
      const jobId = data.job_id;
      videoJobsRef.current[index] = { jobId, cancelled: false };
      let url = null;
      let wasCancelled = false;
      for (let i = 0; i < 160; i++) {
        await sleep(5000);
        if (videoJobsRef.current[index]?.cancelled) { wasCancelled = true; break; }
        try {
          const res = await api.get(`/video-job/${jobId}`);
          if (res.data.status === "done") { url = res.data.video_url; break; }
          if (res.data.status === "cancelled") { wasCancelled = true; break; }
          if (res.data.status === "error") throw new Error(res.data.error || "video generation failed");
        } catch (e) {
          if (e?.message && !e?.response) throw e;
        }
      }
      if (wasCancelled) { toast.info(`Scene ${index + 1} video cancelled`); return; }
      if (!url) throw new Error("Video timed out");
      // The backend video job already persisted this URL atomically (dotted-path $set) and
      // cached the MP4 to GridFS. We only update LOCAL state for the UI — we must NOT send it
      // via a full-doc save (payloadOf strips it) to avoid clobbering other scenes' URLs.
      const arr = [...(projectRef.current.scene_video_urls || [])];
      while (arr.length <= index) arr.push(null);
      arr[index] = url;
      const updated = { ...projectRef.current, scene_video_urls: arr };
      projectRef.current = updated;
      setProject(updated);
      toast.success(`Scene ${index + 1} video ready`);
    } catch (e) {
      errorToast(e, "Video generation failed");
    } finally {
      delete videoJobsRef.current[index];
      setRegenKeys((k) => k.filter((x) => x !== key));
    }
  };

  const cancelSceneVideo = async (index) => {
    const info = videoJobsRef.current[index];
    if (!info) return;
    info.cancelled = true;  // stops the polling loop immediately
    toast.info(`Cancelling scene ${index + 1} video…`);
    try { await api.post(`/video-job/${info.jobId}/cancel`); } catch (e) { /* best-effort */ }
    setRegenKeys((k) => k.filter((x) => x !== `video-${index}`));
  };

  const generateAllVideos = async () => {
    const ids = project.scene_image_ids || [];
    const targets = (project.scenes || []).map((_, i) => i).filter((i) => ids[i]);
    if (targets.length === 0) { toast.error("Generate scene images first"); return; }
    toast.info(`Generating videos for ${targets.length} scene(s)…`);
    for (const i of targets) {
      if ((projectRef.current.scene_video_urls || [])[i]) continue;
      await generateSceneVideo(i);
    }
    toast.success("All scene videos done");
  };

  const toggleApprove = async (index) => {
    const arr = [...(projectRef.current.scene_approved || [])];
    while (arr.length <= index) arr.push(false);
    arr[index] = !arr[index];
    const updated = { ...projectRef.current, scene_approved: arr };
    projectRef.current = updated;
    setProject(updated);
    try {
      // Atomic single-index write — never clobbers other scenes' approval (no full-doc save).
      await api.post(`/projects/${projectRef.current.project_id}/scene-flag`, {
        field: "scene_approved", index, value: arr[index],
      });
      toast.success(arr[index] ? `Scene ${index + 1} approved` : `Scene ${index + 1} approval removed`);
    } catch (e) {
      errorToast(e, "Could not save approval");
    }
  };

  const toggleImageApprove = async (index) => {
    const arr = [...(projectRef.current.scene_image_approved || [])];
    while (arr.length <= index) arr.push(false);
    arr[index] = !arr[index];
    const updated = { ...projectRef.current, scene_image_approved: arr };
    projectRef.current = updated;
    setProject(updated);
    try {
      await api.post(`/projects/${projectRef.current.project_id}/scene-flag`, {
        field: "scene_image_approved", index, value: arr[index],
      });
      toast.success(arr[index] ? `Scene ${index + 1} image approved` : `Scene ${index + 1} image approval removed`);
    } catch (e) {
      errorToast(e, "Could not save approval");
    }
  };

  const generateThumbnailVideo = async (duration = 3, resolution = "1080p", modelChoice = "2.0", mods = "") => {
    if (!project.thumbnail_image_id) { toast.error("Generate the thumbnail image first"); return; }
    let current = project;
    if (dirty || !current.project_id) { current = await saveProject(current); if (!current) return; }
    const key = "thumbnail-video";
    setRegenKeys((k) => [...k, key]);
    try {
      const m = (mods || "").trim();
      const prompt = m ? `ADDITIONAL CHANGES REQUESTED (apply to the motion): ${m}` : "";
      const { data } = await api.post("/generate-thumbnail-video", {
        project_id: current.project_id, prompt, dialog: "", model_choice: modelChoice, duration, resolution,
      });
      const jobId = data.job_id;
      let url = null;
      for (let i = 0; i < 170; i++) {
        await sleep(5000);
        try {
          const res = await api.get(`/video-job/${jobId}`);
          if (res.data.status === "done") { url = res.data.video_url; break; }
          if (res.data.status === "error") throw new Error(res.data.error || "video generation failed");
        } catch (e) {
          if (e?.message && !e?.response) throw e;
        }
      }
      if (!url) throw new Error("Video timed out");
      const updated = { ...projectRef.current, thumbnail_video_url: url };
      projectRef.current = updated;
      setProject(updated);
      toast.success("Thumbnail video ready");
    } catch (e) {
      errorToast(e, "Thumbnail video generation failed");
    } finally {
      setRegenKeys((k) => k.filter((x) => x !== key));
    }
  };

  const toggleThumbnailApprove = async () => {
    const updated = { ...projectRef.current, thumbnail_video_approved: !projectRef.current.thumbnail_video_approved };
    projectRef.current = updated;
    setProject(updated);
    try {
      await saveProject(updated);
      toast.success(updated.thumbnail_video_approved ? "Thumbnail video approved" : "Thumbnail video approval removed");
    } catch (e) {
      errorToast(e, "Could not save approval");
    }
  };


  const assembleVideo = async (skipIntro = false) => {
    let current = project;
    if (!current.project_id) { current = await saveProject(current); if (!current) return; }
    const approvedCount = (current.scene_video_urls || []).filter((u, i) => u && (current.scene_approved || [])[i]).length;
    if (approvedCount === 0) { toast.error("Approve at least one scene video first"); return; }
    setAssembling(true);
    setAssembleProgress({ pct: 0, stage: "Queued…" });
    setAssembleLogs([]);
    setAssembleError("");
    setAssembleDone(false);
    setFinalVideoUrl(null);
    setFinalVideoDuration(0);
    try {
      const { data } = await api.post("/assemble-video", { project_id: current.project_id, skip_intro: !!skipIntro });
      const jobId = data.job_id;
      let done = false;
      let warning = "";
      let duration = 0;
      let included = 0;
      let clipReports = [];
      let netFails = 0;
      for (let i = 0; i < 450; i++) {
        await sleep(2000);
        let res;
        try {
          res = await api.get(`/assemble-job/${jobId}`);
        } catch (e) {
          // A 4xx (e.g. job not found) is a real problem — abort. But transient network drops or
          // 5xx from the CPU-busy pod must NOT kill a job that is still running server-side; the
          // background assembly continues regardless of a single failed poll. Tolerate a run of
          // consecutive failures before giving up.
          if (e?.response && e.response.status && e.response.status < 500) throw e;
          netFails += 1;
          if (netFails >= 20) {
            throw new Error("Lost connection while assembling. The video may still be processing on the server — reopen this story in a minute to check for the finished download.");
          }
          continue;
        }
        netFails = 0;
        if (typeof res.data.progress === "number") {
          setAssembleProgress({ pct: res.data.progress, stage: res.data.stage || "" });
        }
        if (Array.isArray(res.data.logs)) setAssembleLogs(res.data.logs);
        if (Array.isArray(res.data.clip_reports)) clipReports = res.data.clip_reports;
        if (res.data.status === "done") { setAssembleProgress({ pct: 100, stage: "Done" }); warning = res.data.warning || ""; duration = res.data.duration || 0; included = (res.data.included_scenes || []).length; done = true; break; }
        if (res.data.status === "error") throw new Error(res.data.error || "assembly failed");
      }
      if (!done) throw new Error("Assembly timed out");
      setAssembleDone(true);
      setFinalVideoUrl(`${API}/final-video/${current.project_id}?t=${Date.now()}`);
      setFinalVideoDuration(duration);
      const failedClips = (clipReports || []).filter((r) => r && r.ok === false);
      if (warning) {
        toast.warning(warning, { duration: 15000 });
      } else if (failedClips.length) {
        const bits = failedClips.map((r) => `${r.kind === "image" ? "intro" : "scene " + r.scene}${r.timed_out ? " (timed out)" : ""}`);
        toast.warning(`Assembled ${included} scene${included === 1 ? "" : "s"} · ${Math.round(duration)}s. Skipped: ${bits.join(", ")}.`, { duration: 15000 });
      } else {
        toast.success(`Full video assembled — ${included} scene${included === 1 ? "" : "s"} · ${Math.round(duration)}s`, { duration: 8000 });
      }
      loadProjects();
    } catch (e) {
      // Keep the assembly log on screen so the user can read/copy exactly what failed
      // (previously the whole panel disappeared the instant the error toast showed).
      setAssembleError(e?.message || "Video assembly failed");
      errorToast(e, "Video assembly failed");
    } finally {
      setAssembling(false);
    }
  };

  return (
    <div className="grain flex h-screen overflow-hidden bg-[#0b0c10] text-[#f3f4f6]">
      {sidebarOpen && (
        <Sidebar
          projects={projects}
          channels={channels}
          currentId={project.project_id}
          onNew={newProject}
          onSelect={selectProject}
          onDelete={deleteProject}
          loading={projectsLoading}
        />
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-20 flex h-16 shrink-0 items-center justify-between gap-4 border-b border-[#272a35] bg-black/60 px-5 backdrop-blur-xl">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              data-testid="toggle-sidebar-btn"
              onClick={() => setSidebarOpen((v) => !v)}
              className="shrink-0 text-[#9ca3af] hover:text-[#f3f4f6]"
            >
              <PanelLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <p className="overline">Story</p>
              <div className="flex min-w-0 items-center gap-2">
                <h1 className="truncate font-display text-base font-semibold tracking-tight leading-none mt-0.5">
                  {project.title || project.idea || "Untitled story"}
                </h1>
                {project.channel_name && (
                  <span
                    data-testid="project-channel-badge"
                    className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-[#00e5ff]/40 bg-[#00e5ff]/10 px-2 py-0.5 text-[10px] font-medium text-[#00e5ff]"
                  >
                    <Tv2 className="h-2.5 w-2.5" /> {project.channel_name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {saving ? (
              <span className="flex items-center gap-1 text-xs text-[#9ca3af]" data-testid="save-status"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</span>
            ) : dirty ? (
              <span className="text-xs text-[#9ca3af]" data-testid="save-status">Auto-saving…</span>
            ) : project.project_id ? (
              <span className="flex items-center gap-1 text-xs text-[#00e5ff]" data-testid="save-status"><Check className="h-3.5 w-3.5" /> Saved</span>
            ) : null}
            <Button
              data-testid="prompt-log-btn"
              onClick={openLogs}
              variant="outline"
              className="rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
            >
              <ScrollText className="mr-2 h-4 w-4 text-[#00e5ff]" /> Prompt log
            </Button>
            <Button
              data-testid="download-package-btn"
              onClick={downloadPackage}
              disabled={exporting || (!project.project_id && !(project.idea || "").trim())}
              variant="outline"
              className="rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
            >
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4 text-[#00e5ff]" />}
              Download package
            </Button>
            <Button
              data-testid="publish-story-btn"
              onClick={togglePublish}
              disabled={!project.project_id || (!finalVideoUrl && !project.published)}
              variant="outline"
              title={!finalVideoUrl && !project.published ? "Assemble the full video first, then you can mark this story as Published" : ""}
              className={`rounded-md ${project.published
                ? "border-[#eab308]/60 bg-[#eab308]/10 text-[#eab308] hover:bg-[#eab308]/20 hover:text-[#eab308]"
                : "border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"}`}
            >
              <BadgeCheck className={`mr-2 h-4 w-4 ${project.published ? "text-[#eab308]" : "text-[#00e5ff]"}`} />
              {project.published ? "Published" : "Mark as Published"}
            </Button>
            <Button
              data-testid="save-project-btn"
              onClick={() => saveProject()}
              disabled={saving}
              variant="outline"
              className="rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save
            </Button>
          </div>
        </header>

        {project.project_id && !project.channel_id && channels.length > 0 && (
          <div
            data-testid="assign-channel-banner"
            className="flex flex-wrap items-center gap-3 border-b border-[#272a35] bg-[#00e5ff]/[0.04] px-5 py-2.5"
          >
            <Tv2 className="h-4 w-4 text-[#00e5ff]" />
            <span className="text-xs text-[#9ca3af]">Assign this story to a channel to reuse its style, tone &amp; references:</span>
            <select
              data-testid="assign-channel-select"
              defaultValue=""
              onChange={(e) => {
                const ch = channels.find((c) => c.channel_id === e.target.value);
                if (ch) assignChannel(ch);
                e.target.value = "";
              }}
              className="rounded-md border border-[#272a35] bg-[#0e1015] px-2 py-1 text-xs text-[#f3f4f6] focus:outline-none focus:ring-1 focus:ring-[#00e5ff]"
            >
              <option value="" disabled>Choose a channel…</option>
              {channels.map((c) => (
                <option key={c.channel_id} value={c.channel_id}>{c.name || "Untitled channel"}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <StoryBoard
            project={project}
            setField={setField}
            onGenerateText={generateText}
            generating={generating}
            onGenerateThumbnail={regenerateThumbnail}
            onGenerateThumbnailVideo={generateThumbnailVideo}
            onToggleThumbnailApprove={toggleThumbnailApprove}
            onGenerateScene={generateScene}
            onGenerateVideo={generateSceneVideo}
            onCancelVideo={cancelSceneVideo}
            onGenerateDialogs={generateDialogs}
            onUploadImage={uploadUserImage}
            onUploadVideo={uploadUserVideo}
            onEditImage={editSceneImage}
            onComposePrompt={composeImagePrompt}
            onToggleApprove={toggleApprove}
            onToggleImageApprove={toggleImageApprove}
            onGenerateAllVideos={generateAllVideos}
            onAssembleVideo={assembleVideo}
            assembling={assembling}
            assembleDone={assembleDone}
            assembleProgress={assembleProgress}
            assembleElapsed={assembleElapsed}
            assembleLogs={assembleLogs}
            assembleError={assembleError}
            onDismissAssembleError={() => { setAssembleError(""); setAssembleLogs([]); setAssembleDone(false); }}
            finalVideoUrl={finalVideoUrl}
            finalVideoDuration={finalVideoDuration}
            channels={channels}
            onSelectChannel={selectChannelForNew}
            regenKeys={regenKeys}
          />
        </div>
      </div>

      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent className="max-w-2xl border-[#272a35] bg-[#14161c] text-[#f3f4f6]" data-testid="prompt-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ScrollText className="h-5 w-5 text-[#00e5ff]" /> Prompt log</DialogTitle>
            <DialogDescription className="text-[#9ca3af]">
              Every prompt sent to the AI for this story's images and videos, newest first.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1" data-testid="prompt-log-list">
            {logsLoading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-[#9ca3af]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : logs.length === 0 ? (
              <p className="py-8 text-center text-sm text-[#9ca3af]">No prompts logged yet. Generate an image or video and they'll appear here.</p>
            ) : (
              logs.map((l) => (
                <div key={l.log_id} className="rounded-md border border-[#272a35] bg-[#0e1015] p-3" data-testid="prompt-log-item">
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[#f3f4f6]">
                      {l.media === "video" ? <Film className="h-3.5 w-3.5 text-[#a855f7]" /> : <ImageIcon className="h-3.5 w-3.5 text-[#00e5ff]" />}
                      {l.label}
                      <span className="rounded bg-[#1d2028] px-1.5 py-0.5 text-[10px] font-normal text-[#9ca3af]">{l.model}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#6b7280]">{new Date(l.created_at).toLocaleString()}</span>
                      <button onClick={() => copyText(l.prompt)} className="text-[11px] text-[#00e5ff] hover:underline" data-testid="copy-log-prompt">Copy</button>
                    </div>
                  </div>
                  {l.extra?.dialog ? (
                    <p className="mb-1 text-[11px] text-[#a855f7]">Dialog: {l.extra.dialog}</p>
                  ) : null}
                  {(l.extra?.duration || l.extra?.resolution) ? (
                    <p className="mb-1 text-[10px] text-[#6b7280]">{l.extra?.duration}s · {l.extra?.resolution}</p>
                  ) : null}
                  {(l.extra?.before_image_id || l.extra?.after_image_id) ? (
                    <div className="mb-2 flex items-end gap-3" data-testid="prompt-log-thumbs">
                      {l.extra?.before_image_id ? (
                        <div className="flex flex-col items-center gap-1">
                          <a href={imageUrl(l.extra.before_image_id)} target="_blank" rel="noreferrer">
                            <img
                              src={imageUrl(l.extra.before_image_id)}
                              alt="before"
                              loading="lazy"
                              className="h-16 w-28 rounded border border-[#272a35] object-cover transition-transform hover:scale-105"
                              data-testid="prompt-log-before-thumb"
                            />
                          </a>
                          <span className="text-[9px] uppercase tracking-wide text-[#6b7280]">Before</span>
                        </div>
                      ) : null}
                      {l.extra?.before_image_id && l.extra?.after_image_id ? (
                        <span className="mb-4 text-[#6b7280]">&rarr;</span>
                      ) : null}
                      {l.extra?.after_image_id ? (
                        <div className="flex flex-col items-center gap-1">
                          <a href={imageUrl(l.extra.after_image_id)} target="_blank" rel="noreferrer">
                            <img
                              src={imageUrl(l.extra.after_image_id)}
                              alt="after"
                              loading="lazy"
                              className="h-16 w-28 rounded border border-[#00e5ff]/40 object-cover transition-transform hover:scale-105"
                              data-testid="prompt-log-after-thumb"
                            />
                          </a>
                          <span className="text-[9px] uppercase tracking-wide text-[#6b7280]">
                            {l.extra?.before_image_id ? "After" : "Result"}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-[#cbd5e1]">{l.prompt}</pre>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
